import { z } from 'zod';
import { DEFAULT_PAGE_SIZE, MAX_PAGE_SIZE, isRealDate, uuidSchema } from './common.js';
import { emailSchema, passwordSchema } from './auth.js';
import { countrySchema } from './org.js';
const text = (max) => z.string().trim().max(max);
/** Blank strings become null so forms can submit empty optional fields. */
const optional = (max) => text(max)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();
const nameText = text(100).min(1);
const isoDate = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD')
    .refine(isRealDate, 'Invalid calendar date');
const todayIso = () => new Date().toISOString().slice(0, 10);
const pastDate = isoDate.refine((v) => v <= todayIso(), 'Date cannot be in the future');
export const GENDERS = ['male', 'female', 'other', 'undisclosed'];
export const BLOOD_GROUPS = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];
export const STUDENT_STATUSES = [
    'active',
    'transferred',
    'graduated',
    'alumni',
    'inactive',
];
export const GUARDIAN_RELATIONSHIPS = [
    'father',
    'mother',
    'guardian',
    'grandparent',
    'sibling',
    'other',
];
/** Allowed lifecycle moves (Promoted is an enrollment event, handled with academic years). */
export const STUDENT_STATUS_TRANSITIONS = {
    active: ['transferred', 'graduated', 'inactive'],
    inactive: ['active', 'transferred'],
    transferred: ['active'],
    graduated: ['alumni', 'active'],
    alumni: [],
};
const phone = text(30).regex(/^[0-9+()\-.\s]{5,30}$/, 'Enter a valid phone number');
const optionalPhone = phone
    .or(z.literal(''))
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();
const optionalEmail = z
    .string()
    .trim()
    .toLowerCase()
    .email()
    .max(254)
    .or(z.literal(''))
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();
export const emergencyContactSchema = z.object({
    name: nameText,
    relationship: text(50).min(1),
    phone,
});
const addressFields = {
    addressLine1: optional(200),
    addressLine2: optional(200),
    city: optional(100),
    state: optional(100),
    postalCode: optional(20),
};
// ---------------------------------------------------------------- students
const studentFields = {
    firstName: nameText,
    middleName: optional(100),
    lastName: nameText,
    dateOfBirth: pastDate,
    gender: z.enum(GENDERS),
    bloodGroup: z.enum(BLOOD_GROUPS).nullable().optional(),
    email: optionalEmail,
    phone: optionalPhone,
    ...addressFields,
    countryCode: countrySchema.nullable().optional(),
    nationality: optional(100),
    previousSchool: optional(200),
    medicalNotes: optional(2000),
    emergencyContacts: z.array(emergencyContactSchema).max(5),
    house: optional(50),
    admissionDate: isoDate,
};
export const enrollmentInputSchema = z.object({
    /** Defaults to the school's current academic year. */
    academicYearId: uuidSchema.optional(),
    classId: uuidSchema,
    sectionId: uuidSchema.nullable().optional(),
    rollNumber: optional(20),
});
export const createStudentSchema = z.object({
    schoolId: uuidSchema,
    ...studentFields,
    emergencyContacts: studentFields.emergencyContacts.default([]),
    admissionDate: studentFields.admissionDate.optional(),
    enrollment: enrollmentInputSchema.optional(),
});
export const updateStudentSchema = z
    .object(studentFields)
    .partial()
    .refine((v) => Object.keys(v).length > 0, 'At least one field is required');
export const changeStudentStatusSchema = z.object({
    status: z.enum(STUDENT_STATUSES),
    reason: text(500).optional(),
});
export const STUDENT_SORT_FIELDS = [
    'name',
    'admissionNumber',
    'admissionDate',
    'createdAt',
];
export const listStudentsQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(MAX_PAGE_SIZE).default(DEFAULT_PAGE_SIZE),
    schoolId: uuidSchema.optional(),
    classId: uuidSchema.optional(),
    sectionId: uuidSchema.optional(),
    academicYearId: uuidSchema.optional(),
    status: z.enum(STUDENT_STATUSES).optional(),
    gender: z.enum(GENDERS).optional(),
    q: text(100).optional(),
    sort: z
        .enum([
        'name',
        '-name',
        'admissionNumber',
        '-admissionNumber',
        'admissionDate',
        '-admissionDate',
        'createdAt',
        '-createdAt',
    ])
        .default('name'),
});
export const bulkUpdateStudentsSchema = z.object({
    ids: z.array(uuidSchema).min(1).max(500),
    changes: z
        .object({ house: text(50).nullable(), status: z.enum(STUDENT_STATUSES) })
        .partial()
        .refine((v) => Object.keys(v).length > 0, 'At least one change is required'),
});
// ---------------------------------------------------------------- guardians
const guardianFields = {
    firstName: nameText,
    lastName: nameText,
    email: optionalEmail,
    phone: optionalPhone,
    occupation: optional(100),
    ...addressFields,
};
export const createGuardianSchema = z.object({
    ...guardianFields,
    organizationId: uuidSchema.optional(),
});
export const updateGuardianSchema = z
    .object(guardianFields)
    .partial()
    .refine((v) => Object.keys(v).length > 0, 'At least one field is required');
export const listGuardiansQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(MAX_PAGE_SIZE).default(DEFAULT_PAGE_SIZE),
    q: text(100).optional(),
    organizationId: uuidSchema.optional(),
});
export const guardianLinkFlags = {
    relationship: z.enum(GUARDIAN_RELATIONSHIPS),
    isPrimary: z.boolean().default(false),
    isEmergencyContact: z.boolean().default(false),
};
/** Link an existing guardian, or create a new one inline. */
export const linkGuardianSchema = z
    .object({
    guardianId: uuidSchema.optional(),
    guardian: z.object(guardianFields).optional(),
    ...guardianLinkFlags,
})
    .refine((v) => (v.guardianId === undefined) !== (v.guardian === undefined), {
    message: 'Provide either guardianId or guardian',
    path: ['guardianId'],
});
export const updateGuardianLinkSchema = z
    .object({
    relationship: z.enum(GUARDIAN_RELATIONSHIPS),
    isPrimary: z.boolean(),
    isEmergencyContact: z.boolean(),
})
    .partial()
    .refine((v) => Object.keys(v).length > 0, 'At least one field is required');
export const createGuardianLoginSchema = z.object({ password: passwordSchema });
// ---------------------------------------------------------------- staff
const staffFields = {
    firstName: nameText,
    lastName: nameText,
    dateOfBirth: pastDate.nullable().optional(),
    gender: z.enum(GENDERS).nullable().optional(),
    email: optionalEmail,
    phone: optionalPhone,
    addressLine1: optional(200),
    city: optional(100),
    staffType: z.enum(['teaching', 'non_teaching']),
    designation: optional(100),
    department: optional(100),
    joiningDate: isoDate,
};
export const createStaffSchema = z.object({
    schoolId: uuidSchema,
    ...staffFields,
    staffType: staffFields.staffType.default('teaching'),
    joiningDate: staffFields.joiningDate.optional(),
});
export const updateStaffSchema = z
    .object({ ...staffFields, status: z.enum(['active', 'on_leave', 'exited']) })
    .partial()
    .refine((v) => Object.keys(v).length > 0, 'At least one field is required');
export const listStaffQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(MAX_PAGE_SIZE).default(DEFAULT_PAGE_SIZE),
    schoolId: uuidSchema.optional(),
    status: z.enum(['active', 'on_leave', 'exited']).optional(),
    staffType: z.enum(['teaching', 'non_teaching']).optional(),
    q: text(100).optional(),
});
// ---------------------------------------------------------------- classes
export const createClassSchema = z.object({
    name: text(100).min(1),
    sortOrder: z.number().int().min(0).max(1000).default(0),
    streamId: uuidSchema.nullable().optional(),
});
export const updateClassSchema = z
    .object({
    name: text(100).min(1),
    sortOrder: z.number().int().min(0).max(1000),
    streamId: uuidSchema.nullable(),
})
    .partial()
    .refine((v) => Object.keys(v).length > 0, 'At least one field is required');
export const createSectionSchema = z.object({
    name: text(50).min(1),
    capacity: z.number().int().min(1).max(500).nullable().optional(),
});
export const updateSectionSchema = z
    .object({ name: text(50).min(1), capacity: z.number().int().min(1).max(500).nullable() })
    .partial()
    .refine((v) => Object.keys(v).length > 0, 'At least one field is required');
// ---------------------------------------------------------------- admissions
export const ENQUIRY_STATUSES = ['new', 'contacted', 'application_issued', 'closed'];
export const APPLICATION_STATUSES = [
    'submitted',
    'under_review',
    'approved',
    'rejected',
    'admitted',
];
/** Allowed transitions. `admitted` is reached only through the admit action (creates the student). */
export const ENQUIRY_TRANSITIONS = {
    new: ['contacted', 'application_issued', 'closed'],
    contacted: ['application_issued', 'closed'],
    application_issued: ['closed'],
    closed: ['new'],
};
export const APPLICATION_TRANSITIONS = {
    submitted: ['under_review', 'rejected'],
    under_review: ['approved', 'rejected', 'submitted'],
    approved: ['rejected'],
    rejected: ['under_review'],
    admitted: [],
};
export const createEnquirySchema = z
    .object({
    studentName: nameText,
    parentName: nameText,
    contactPhone: optionalPhone,
    contactEmail: optionalEmail,
    interestedClass: optional(100),
    source: optional(100),
    notes: optional(2000),
    followUpDate: isoDate.nullable().optional(),
})
    .refine((v) => !!v.contactPhone || !!v.contactEmail, {
    message: 'Provide a phone number or an email address',
    path: ['contactPhone'],
});
export const updateEnquirySchema = z
    .object({
    studentName: nameText,
    parentName: nameText,
    contactPhone: optionalPhone,
    contactEmail: optionalEmail,
    interestedClass: optional(100),
    source: optional(100),
    notes: optional(2000),
    followUpDate: isoDate.nullable(),
})
    .partial()
    .refine((v) => Object.keys(v).length > 0, 'At least one field is required');
export const applicantSchema = z.object({
    firstName: nameText,
    middleName: optional(100),
    lastName: nameText,
    dateOfBirth: pastDate,
    gender: z.enum(GENDERS),
    bloodGroup: z.enum(BLOOD_GROUPS).nullable().optional(),
    previousSchool: optional(200),
    nationality: optional(100),
});
export const applicationGuardianSchema = z.object({
    ...guardianFields,
    relationship: z.enum(GUARDIAN_RELATIONSHIPS),
    isPrimary: z.boolean().default(false),
    isEmergencyContact: z.boolean().default(false),
});
export const createApplicationSchema = z.object({
    enquiryId: uuidSchema.optional(),
    academicYearId: uuidSchema,
    classId: uuidSchema,
    applicant: applicantSchema,
    guardians: z
        .array(applicationGuardianSchema)
        .min(1, 'At least one parent/guardian is required')
        .max(4),
});
export const updateApplicationSchema = z
    .object({
    academicYearId: uuidSchema,
    classId: uuidSchema,
    applicant: applicantSchema,
    guardians: z.array(applicationGuardianSchema).min(1).max(4),
})
    .partial()
    .refine((v) => Object.keys(v).length > 0, 'At least one field is required');
export const transitionSchema = z.object({
    to: z.string().min(1).max(30),
    note: text(1000).optional(),
});
export const admitApplicationSchema = z.object({
    sectionId: uuidSchema.nullable().optional(),
    rollNumber: optional(20),
});
export const listAdmissionsQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(MAX_PAGE_SIZE).default(DEFAULT_PAGE_SIZE),
    status: z.string().max(30).optional(),
    q: text(100).optional(),
});
// Re-export a helper used by importers.
export { emailSchema };
//# sourceMappingURL=people.js.map